// SEO.tsx - Componente para gestionar meta tags y SEO en React
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { keywordStrategy, serviceKeywords } from '../data/keywords';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
  noIndex?: boolean;
  canonicalUrl?: string;
}

const SEO: React.FC<SEOProps> = ({
  title = "Algorithmia - Desarrollo de Software a Medida en Córdoba, Argentina | Sistemas de Gestión PWA",
  description = "Expertos en desarrollo de software personalizado en Córdoba. Creamos sistemas de gestión empresarial, apps PWA para mantenimiento industrial y software de seguridad laboral. +50 proyectos exitosos en Argentina. ¡Solicita tu demo gratis!",
  keywords: propKeywords,
  image = "https://algorithmia.com.ar/og-image.jpg",
  url = "https://algorithmia.com.ar",
  type = "website",
  publishedTime = "2024-01-01",
  modifiedTime = new Date().toISOString().split('T')[0],
  author = "Algorithmia Team",
  section = "Desarrollo de Software",
  noIndex = false,
  canonicalUrl
}) => {
  // Generar meta keywords
  let metaKeywords: string;
  
  if (propKeywords) {
    metaKeywords = propKeywords;
  } else {
    metaKeywords = keywordStrategy.primary
      .slice(0, 8)
      .map(k => k.keyword)
      .join(', ');
  }

  // URL canónica (si se proporciona una específica, usarla)
  const canonical = canonicalUrl || url;

  return (
    <Helmet>
      {/* Meta Tags Básicos */}
      <meta charSet="utf-8" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="author" content={author} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      
      {/* Robots control */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description.substring(0, 160)} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Algorithmia - Desarrollo de Software a Medida en Córdoba" />
      <meta property="og:site_name" content="Algorithmia" />
      <meta property="og:locale" content="es_AR" />
      <meta property="article:published_time" content={publishedTime} />
      <meta property="article:modified_time" content={modifiedTime} />
      <meta property="article:author" content={author} />
      <meta property="article:section" content={section} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title.substring(0, 60)} />
      <meta name="twitter:description" content={description.substring(0, 160)} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content="Algorithmia - Desarrollo de Software a Medida en Córdoba" />
      <meta name="twitter:site" content="@algorithmia_ar" />
      <meta name="twitter:creator" content="@algorithmia_ar" />
      
      {/* Meta Tags Adicionales */}
      <meta name="theme-color" content="#4ADE80" />
      <meta name="msapplication-TileColor" content="#0E2544" />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="geo.region" content="AR-X" />
      <meta name="geo.placename" content="Córdoba, Argentina" />
      <meta name="geo.position" content="-31.4201;-64.1888" />
      <meta name="ICBM" content="-31.4201, -64.1888" />
      <meta name="language" content="Spanish" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Hreflang para Argentina y otros países */}
      <link rel="alternate" href={url} hrefLang="es-ar" />
      <link rel="alternate" href={url} hrefLang="es" />
      <link rel="alternate" href={url} hrefLang="x-default" />
      
      {/* Favicon y App Icons */}
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/manifest.json" />
      <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#4ADE80" />
      
      {/* Preconnect para recursos externos */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://images.unsplash.com" />
      
      {/* JSON-LD Schema.org - Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://algorithmia.com.ar/#organization",
          "name": "Algorithmia",
          "alternateName": "Algorithmia - Desarrollo de Software",
          "url": "https://algorithmia.com.ar",
          "logo": {
            "@type": "ImageObject",
            "url": "https://algorithmia.com.ar/logo512.png",
            "width": 512,
            "height": 512,
            "caption": "Logo de Algorithmia - Desarrollo de Software en Córdoba"
          },
          "description": description.substring(0, 160),
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Córdoba",
            "addressRegion": "Córdoba",
            "addressCountry": "AR"
          },
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "telephone": "+54-351-2489015",
              "contactType": "customer service",
              "areaServed": "AR",
              "availableLanguage": ["Spanish"]
            },
            {
              "@type": "ContactPoint",
              "telephone": "+54-351-2021393",
              "contactType": "technical support",
              "areaServed": "AR",
              "availableLanguage": ["Spanish"]
            }
          ],
          "sameAs": [
            "https://www.linkedin.com/company/algorithmia",
            "https://twitter.com/algorithmia_ar",
            "https://www.instagram.com/algorithmia.ar",
            "https://www.facebook.com/algorithmia.ar"
          ],
          "foundingDate": "2020",
          "numberOfEmployees": {
            "@type": "QuantitativeValue",
            "minValue": 11,
            "maxValue": 50
          },
          "email": "contacto@algorithmia.com.ar",
          "areaServed": ["Córdoba", "Argentina"],
          "keywords": metaKeywords
        })}
      </script>
      
      {/* JSON-LD Schema.org - LocalBusiness */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://algorithmia.com.ar/#localbusiness",
          "name": "Algorithmia - Desarrollo de Software",
          "image": image,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Córdoba",
            "addressRegion": "Córdoba",
            "addressCountry": "AR"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "-31.4201",
            "longitude": "-64.1888"
          },
          "url": "https://algorithmia.com.ar",
          "telephone": "+54-351-2489015",
          "priceRange": "$$",
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "09:00",
              "closes": "18:00"
            }
          ],
          "areaServed": ["Córdoba Capital", "Gran Córdoba", "Provincia de Córdoba"],
          "hasMap": "https://goo.gl/maps/..."
        })}
      </script>
      
      {/* JSON-LD Schema.org - Services */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": serviceKeywords ? Object.entries(serviceKeywords).map(([key, keywords], index) => ({
            "@type": "Service",
            "position": index + 1,
            "name": key.replace(/([A-Z])/g, ' $1').trim(),
            "description": `Servicio de ${key.replace(/([A-Z])/g, ' $1').toLowerCase()} especializado para empresas en Argentina`,
            "provider": {
              "@type": "Organization",
              "name": "Algorithmia",
              "sameAs": "https://algorithmia.com.ar"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Argentina"
            },
            "category": "Desarrollo de Software",
            "keywords": keywords.keywords.join(', '),
            "offers": {
              "@type": "Offer",
              "availability": "https://schema.org/InStock"
            }
          })) : []
        })}
      </script>
      
      {/* JSON-LD Schema.org - Breadcrumb */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": "https://algorithmia.com.ar"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Servicios de Desarrollo",
              "item": "https://algorithmia.com.ar/#servicios"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Portafolio de Proyectos",
              "item": "https://algorithmia.com.ar/#proyectos"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Contacto",
              "item": "https://algorithmia.com.ar/#contacto"
            }
          ]
        })}
      </script>
      
      {/* JSON-LD Schema.org - FAQ */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "¿Qué servicios de desarrollo de software ofrecen en Córdoba?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Ofrecemos desarrollo de software a medida, sistemas de gestión empresarial, apps PWA para mantenimiento industrial, software de seguridad laboral, y más. Todos nuestros servicios están disponibles para empresas en Córdoba y toda Argentina."
              }
            },
            {
              "@type": "Question",
              "name": "¿Trabajan con empresas de todo Argentina?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sí, trabajamos con empresas de todo el país. Nuestras soluciones son 100% remotas y nos adaptamos a las necesidades de cada cliente, sin importar su ubicación en Argentina."
              }
            },
            {
              "@type": "Question",
              "name": "¿Qué es una aplicación PWA y por qué es importante?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Una PWA (Progressive Web App) es una aplicación que funciona como sitio web pero con capacidades de app nativa: funciona offline, se puede instalar, envía notificaciones y se actualiza automáticamente. Es ideal para empresas que necesitan movilidad sin depender de tiendas de aplicaciones."
              }
            }
          ]
        })}
      </script>
      
      {/* JSON-LD Schema.org - WebSite */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://algorithmia.com.ar/#website",
          "url": "https://algorithmia.com.ar",
          "name": "Algorithmia - Desarrollo de Software a Medida",
          "description": description.substring(0, 160),
          "publisher": {
            "@id": "https://algorithmia.com.ar/#organization"
          },
          "inLanguage": "es-AR",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://algorithmia.com.ar/search?q={search_term_string}"
            },
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
    </Helmet>
  );
};

export default SEO;